<template>
  <div
    id="review-form-container"
    class="pt-8"
  >
    <div class="box-theme box-theme--primary box-theme--shadow-against-teal-background box-theme--border-against-teal-background box-theme--width-xl box-theme--padding-md overflow-hidden relative m-auto text-slate-600">

      <!-- title-->
      <h2 class="text-2xl font-bold">{{ $t('messages_nl.partials.cancellationReview.title', { companyName }) }}</h2>
      <hr class="mt-4 bg-box-against-white-background border-slate-300">

      <adivare-collapse
        breakpoint="<md"
        :offset="400"
        expanded
      >
        <template v-slot:toggle="slotProps">
          <div class="flex items-center justify-center text-lg mt-6">
            <p class="text-red">{{ $t('messages_nl.cancellation.view.cancellationReview.writeReview') }}</p>
            <img
              :src="$asset('img/common/arrows/dropdown-arrow-to-bottom-red.svg')"
              :alt="$t('messages_nl.images.arrow')"
              :class="['transition-all', {'rotate-180': slotProps.expanded}]"
              width="35"
              height="35"
              loading="lazy"
            >
          </div>
        </template>
        <template #collapsibleContent>

          <!-- review form -->
          <form
            class="grid grid-cols-6 gap-x-5 gap-y-2 my-2"
            name="cancellation_review"
            @submit.prevent="submitReview"
          >

            <!-- review form: flash messages -->
            <adivare-flashes
              class="col-span-6"
              :show-only-subjects="['cancellation-review']"
              scroll-to-flash
            />

            <!-- review form: rating -->
            <fieldset class="col-span-6 my-4 flex flex-col items-center md:items-start">
              <label
                for="cancellation_review_stars"
                class="font-medium text-slate-600 text-lg pt-0 text-center md:text-left"
                ref="rating"
              >
                {{ setRequiredField($t('messages_nl.label.cancellation.rating')) }}
              </label>
              <div class="relative">
                <adivare-review-stars
                  id="cancellation_review_stars"
                  v-model="rating"
                  size="lg"
                />
                <img
                  :src="$asset('img/cancellation/tap.png')"
                  :alt="$t('messages_nl.form.review.rating.icon.alt')"
                  :title="$t('messages_nl.form.review.rating.icon.title')"
                  loading="lazy"
                  class="oscillate-110 absolute -right-12 top-4 opacity-60"
                  width="35"
                  height="35"
                >
              </div>

            </fieldset>

            <!-- review form: name -->
            <fieldset class="col-span-6 md:col-span-3 flex flex-col">
              <label
                for="cancellation_review_name"
                :class="['p-0 text-base text-slate-600 font-medium', showLabelLegend(name)]"
                ref="name"
                id="cancellation_review_name-label"
              >
                {{ setRequiredField($t('messages_nl.label.customerName')) }}
              </label>
              <input
                id="cancellation_review_name"
                :class="[
                  'form--texttype w-full',
                  { 'border-red': null !== nameErrorMessage },
                ]"
                type="text"
                v-model.trim="name"
                autocomplete="name"
                required
                :placeholder="setRequiredField($t('messages_nl.label.customerName'))"
                aria-labelledby="cancellation_review_name-label"
              >
              <adivare-alert
                class="mt-1"
                :messages="nameErrorMessage"
                size="xs"
                show-icon
              />
            </fieldset>

            <!-- review form: email -->
            <fieldset class="col-span-6 md:col-span-3 flex flex-col">
              <label
                for="cancellation_review_email"
                :class="['p-0 text-base text-slate-600 font-medium', showLabelLegend(email)]"
                ref="email"
                id="cancellation_review_email-label"
              >
                {{ setRequiredField($t('messages_nl.form.review.email')) }}
              </label>
              <input
                id="cancellation_review_email"
                :class="[
                  'form--texttype w-full',
                  { 'border-red': null !== emailErrorMessage },
                ]"
                type="text"
                v-model.trim="email"
                autocomplete="email"
                required
                :placeholder="setRequiredField($t('messages_nl.form.review.email'))"
                aria-labelledby="cancellation_review_email-label"
              >
              <adivare-alert
                class="mt-1"
                :messages="emailErrorMessage"
                size="xs"
                show-icon
              />
            </fieldset>

            <!-- review form: city -->
            <fieldset class="col-span-6">
              <label
                for="cancellation_review_city"
                :class="['p-0 text-base text-slate-600 font-medium', showLabelLegend(city)]"
                ref="city"
                id="cancellation_review_city-label"
              >
                {{ setRequiredField($t('messages_nl.label.customerCity')) }}
              </label>
              <input
                id="cancellation_review_city"
                :class="[
                  'form--texttype w-full',
                  { 'border-red': null !== cityErrorMessage },
                ]"
                type="text"
                v-model.trim="city"
                autocomplete="address-level2"
                maxlength="100"
                required
                :placeholder="setRequiredField($t('messages_nl.label.customerCity'))"
                aria-labelledby="cancellation_review_city-label"
              >
              <adivare-alert
                class="mt-1"
                :messages="cityErrorMessage"
                size="xs"
                show-icon
              />
            </fieldset>

            <!-- review form: description -->
            <fieldset class="col-span-6">
              <label
                for="cancellation_review_description"
                :class="['p-0 text-base text-slate-600 font-medium', showLabelLegend(description)]"
                ref="description"
                id="cancellation_review_description-label"
              >
                {{ setRequiredField($t('messages_nl.label.cancellation.description', { companyName })) }}
              </label>
              <textarea
                id="cancellation_review_description"
                :class="[
                  'form--textareatype w-full',
                  { 'border-red': null !== descriptionErrorMessage },
                ]"
                type="text"
                v-model.trim="description"
                :placeholder="setRequiredField($t('messages_nl.label.cancellation.description', {companyName}))"
                rows="6"
                maxlength="3000"
                required
                aria-labelledby="cancellation_review_description-label"
              />
              <adivare-alert
                class="mt-1"
                :messages="descriptionErrorMessage"
                size="xs"
                show-icon
              />
            </fieldset>
          </form>

          <!-- review form: submit button -->
          <button
            type="button"
            class="click-theme click-theme--primary click-theme--size-md mt-8 mb-2"
            @click="submitReview"
          >
            <span
              v-if="formLoading"
              class="loader-bars loader-bars--size-md click-theme__loader"
            ></span>
            <span :style="{visibility: !formLoading ? 'visible' : 'hidden'}">
              {{ $t('messages_nl.cancellation.view.cancellationReview.submitReview') }}
            </span>
          </button>

          <!-- review form: legenda -->
          <p class="mt-6 text-xs text-slate-600">{{ $t('messages_nl.partials.letter.required') }}</p>
        </template>
      </adivare-collapse>
    </div>
  </div>
</template>

<script>
import client from "../../../axios-client";
import AdivareAlert from '../../../components/alert';
import AdivareFormErrorBlock from './form-error-block';
import readCookie from "../../../../js/util/cookie"
import { cancellationReviewData } from "../../../../js/util/cookie"
import AdivareReviewStars from "../../../components/form/review-stars";
import submitMixin from "../../mixins/submit-mixin";
import AdivareCollapse from "/assets/vue/components/collapse";
import AdivareFlashes from "../ui/flashes.vue";
import { mapGetters } from 'vuex';

export default {
  name: "cancellation-review-form",
  components: {
    AdivareReviewStars,
    AdivareFormErrorBlock,
    AdivareCollapse,
    AdivareFlashes,
    AdivareAlert,
  },
  mixins: [
    submitMixin,
  ],
  data() {
    return {
      hasSubmitted: false,
      rating: 3,
      name: null,
      email: null,
      city: null,
      description: null,
      formLoading: false,
      errorMessages: {
        name: null,
        email: null,
        city: null,
        description: null,
      },
      submissionValid: null,
      showErrors: false,
    };
  },
  computed: {
    ...mapGetters([
      'totalCancellationReviews',
      'cancellationReviewsLoading',
    ]),
    companyName() {
      return this.$store.getters.companyName;
    },
    submissionMessage() {
      return this.submissionValid ? this.$t('messages_nl.form.review.success') : this.$t('messages_nl.form.review.error');
    },
    submissionVariant() {
      return this.submissionValid ? 'success' : 'danger';
    },
    formValid() {
      return null !== this.name && null !== this.email && 0 !== this.rating;
    },
    nameErrorMessage() {
      return this.errorMessages.name;
    },
    emailErrorMessage() {
      return this.errorMessages.email;
    },
    cityErrorMessage() {
      return this.errorMessages.city;
    },
    descriptionErrorMessage() {
      return this.errorMessages.description;
    },
  },
  mounted() {
    this.readCookieData();
  },
  methods: {
    submitReview() {
      this.showErrors = true;
      if (false === this.hasSubmitted) {
        this.clearFormErrors();
        this.formLoading = true;
        client
          .fetchIp()
          .then(response => {
            client
              .submitCancellationReview(this.$store.getters.cancellationId, {
                name: this.name,
                email: this.email,
                rating: this.rating,
                city: this.city,
                description: this.description,
                ipAddress: response.data.ip,
              })
              .then(() => {
                this.formLoading = false;
                this.submissionValid = true
                this.hasSubmitted = true;
                this.$store.dispatch('addFlash', {
                  type: this.submissionVariant,
                  message: this.submissionMessage,
                  subject: 'cancellation-review',
                });
                this.clearForm();
              })
              .catch(error => {
                this.formLoading = false;
                this.handleBadRequest(error);
              });
          })
      }
    },
    handleBadRequest(error) {
      const data = error.response.data.data;
      const errorKeys = Object.keys(data);
      const knownKeys = ['email', 'name', 'city', 'description'];
      if (errorKeys.length === 0) {
        this.$store.dispatch('addFlash', {
          type: 'danger',
          message: this.$t('messages_nl.form.review.reviewNotUnique'),
          subject: 'cancellation-review',
        });
      } else if (knownKeys.some(key => errorKeys.includes(key))) {
        let hasScrolled = false;
        errorKeys.forEach(key => {
          const ref = this.$refs[key];
          if (false === hasScrolled && undefined !== ref) {
            ref.scrollIntoView();
            scrollBy(0, -140); //adjust for header
            hasScrolled = true;
          }
          this.errorMessages[key] = data[key];
        });
      } else {
        console.debug(`Falling back to default error message: ${error.response.data.data}`)
        this.handleSubmitError(error);
      }
    },
    showLabelLegend(value) {
      return null !== value && undefined !== value && '' !== value ? 'visible' : 'invisible';
    },
    clearForm() {
      this.name = null;
      this.email = null;
      this.city = null;
      this.description = null;
    },
    clearFormErrors() {
      Object.keys(this.errorMessages).forEach(key => this.errorMessages[key] = null);
    },
    setRequiredField(text) {
      return `${text} *`;
    },
    readCookieData() {
      try {
        const cookieData = readCookie(cancellationReviewData);
        this.name = cookieData.name;
        this.email = cookieData.email;
        this.city = cookieData.city;
      } catch (e) {
      }
    },
  },
}
</script>
